import { computed } from 'vue'

/**
 * Encapsulates premium-lock checks for lens interactions.
 *
 * @param {Object}   options
 * @param {Ref}      options.leftDetail     - ref for the left (or only) selected lens detail
 * @param {Ref}      [options.rightDetail]  - ref for the right selected lens detail; omit for single-lens views
 * @param {Ref}      options.isPremiumUser  - ref/computed for whether the current user has premium access
 * @param {Function} options.onLocked       - called whenever a locked action is attempted
 *
 * @returns {{
 *   isLeftPremium, isRightPremium,
 *   handleTransmissionClick, handleFocalLengthClick,   // (event, side = 'LEFT')
 *   guardSave,   // (side = 'LEFT') → true if blocked
 *   guardPlay,   // () → true if either side is blocked
 * }}
 */
export function usePremiumLock({ leftDetail, rightDetail, isPremiumUser, onLocked }) {
  const isPremium = detail => {
    if (!detail) return false
    const lens = detail.value
    return !!(lens && lens.isPremium === true && !isPremiumUser.value)
  }

  const isLeftPremium = computed(() => isPremium(leftDetail))
  const isRightPremium = computed(() => isPremium(rightDetail))

  const checkLock = (event, side = 'LEFT') => {
    const locked = side === 'LEFT' ? isLeftPremium.value : isRightPremium.value
    if (locked) {
      event.preventDefault()
      onLocked()
    }
  }

  const guardSave = (side = 'LEFT') => {
    const locked = side === 'LEFT' ? isLeftPremium.value : isRightPremium.value
    if (locked) {
      onLocked()
      return true
    }
    return false
  }

  const guardPlay = () => {
    if (isLeftPremium.value || isRightPremium.value) {
      onLocked()
      return true
    }
    return false
  }

  return {
    isLeftPremium,
    isRightPremium,
    handleTransmissionClick: checkLock,
    handleFocalLengthClick: checkLock,
    guardSave,
    guardPlay
  }
}
